import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Activity, LogOut } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  isPlatformAdmin: boolean;
}

function LogoOrb() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      <defs>
        <radialGradient id="sidebarOrbGrad" cx="35%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.7" />
          <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.5" />
        </radialGradient>
      </defs>
      <circle cx="18" cy="18" r="16" fill="url(#sidebarOrbGrad)" />
      <circle cx="13" cy="12" r="4" fill="rgba(255,255,255,0.2)" />
    </svg>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
}

function NavItem({ to, icon, label }: NavItemProps) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
          isActive
            ? 'bg-primary-500/15 text-primary-300'
            : 'text-surface-400 hover:text-surface-200 hover:bg-white/[0.04]',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

export default function Sidebar({ isPlatformAdmin }: SidebarProps) {
  const { email, logout } = useAuth();

  return (
    <aside className="w-64 flex flex-col sidebar flex-shrink-0">
      {/* Brand */}
      <div className="p-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <LogoOrb />
          <div>
            <h1 className="font-display font-bold text-white text-base leading-tight">
              Kai&amp;Co
            </h1>
            <p className="text-[10px] uppercase tracking-[0.15em] text-primary-400/60 font-semibold">
              Operations
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        <p className="text-[10px] uppercase tracking-[0.15em] text-surface-600 font-semibold px-3 mb-2">
          Dashboard
        </p>
        <NavItem
          to="/"
          icon={<LayoutDashboard className="w-4 h-4" />}
          label="Overview"
        />
        {isPlatformAdmin && (
          <NavItem
            to="/platform"
            icon={<Activity className="w-4 h-4" />}
            label="Platform Health"
          />
        )}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-white/[0.05]">
        <div className="px-3 py-2 mb-1">
          <p className="text-xs text-surface-400 truncate">{email}</p>
          {isPlatformAdmin && (
            <p className="text-[10px] text-primary-400/70 font-medium mt-0.5">Platform Admin</p>
          )}
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-surface-500 hover:text-surface-200 hover:bg-white/[0.04] rounded-lg transition-all"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
